import { useState, useEffect } from "react";
import moment from "moment";
import { Link } from "react-router";
import {
  ExternalLink,
  Copy,
  Check,
  Clock,
  UserCheck,
  Loader,
  RotateCcw,
  CalendarClock,
  SearchCheck,
  CheckCircle2,
  BadgeCheck,
  XCircle,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import type { NoteAudience } from "@shyne/shared/api";
import { AppointmentJoin, AppointmentLogJoin, AppointmentsRepository } from "@/repositories/appointments.repository";
import { audienceLabel, isNote } from "@/repositories/appointment-history";
import NoteAudiencePicker from "@/components/shared/NoteAudiencePicker";
import { AppointmentMetaData, appointment_status } from "@/models/appointment.model";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { format } from "date-fns";

// Mirrors the colors in AppointmentStatusBadge, exposed as a banner strip
const STATUS_CONFIG: Record<
  appointment_status,
  { label: string; icon: React.ReactNode; bg: string; text: string }
> = {
  pending:     { label: "Pending",     icon: <Clock className="w-4 h-4" />,                   bg: "bg-gray-100 dark:bg-gray-800",          text: "text-gray-800 dark:text-gray-200"      },
  assigned:    { label: "Assigned",    icon: <UserCheck className="w-4 h-4" />,                bg: "bg-indigo-100 dark:bg-indigo-900/40",    text: "text-indigo-800 dark:text-indigo-200"  },
  accepted:    { label: "Accepted",    icon: <CheckCircle2 className="w-4 h-4" />,             bg: "bg-lime-100 dark:bg-lime-900/40",        text: "text-lime-800 dark:text-lime-200"      },
  inprogress:  { label: "In Progress", icon: <Loader className="w-4 h-4 animate-spin" />,      bg: "bg-blue-100 dark:bg-blue-900/40",        text: "text-blue-800 dark:text-blue-200"      },
  goback:      { label: "Go Back",     icon: <RotateCcw className="w-4 h-4" />,                bg: "bg-yellow-100 dark:bg-yellow-900/40",    text: "text-yellow-800 dark:text-yellow-200"  },
  rescheduled: { label: "Rescheduled", icon: <CalendarClock className="w-4 h-4" />,            bg: "bg-orange-100 dark:bg-orange-900/40",    text: "text-orange-800 dark:text-orange-200"  },
  inspected:   { label: "Inspected",   icon: <SearchCheck className="w-4 h-4" />,              bg: "bg-teal-100 dark:bg-teal-900/40",        text: "text-teal-800 dark:text-teal-200"      },
  done:        { label: "Done",        icon: <CheckCircle2 className="w-4 h-4" />,             bg: "bg-green-100 dark:bg-green-900/40",      text: "text-green-800 dark:text-green-200"    },
  completed:   { label: "Completed",   icon: <BadgeCheck className="w-4 h-4" />,               bg: "bg-emerald-100 dark:bg-emerald-900/40",  text: "text-emerald-800 dark:text-emerald-200"},
  cancelled:   { label: "Cancelled",   icon: <XCircle className="w-4 h-4" />,                  bg: "bg-red-100 dark:bg-red-900/40",          text: "text-red-800 dark:text-red-200"        },
};

interface AppointmentQuickViewSheetProps {
  appointment: AppointmentJoin | null;
  open: boolean;
  onClose: () => void;
  /** Vendor role: pass the assignment dropdown. PMC role: omit (shows read-only vendor name). */
  assignmentSlot?: React.ReactNode;
  userRole?: "pmc" | "vendor";
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2.5">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-medium text-right">{value}</span>
    </div>
  );
}

function SlotRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-1.5">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <div>{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-0.5">
        {title}
      </p>
      <div className="rounded-lg border divide-y overflow-hidden">{children}</div>
    </div>
  );
}

export default function AppointmentQuickViewSheet({
  appointment: appt,
  open,
  onClose,
  assignmentSlot,
  userRole = "pmc",
}: AppointmentQuickViewSheetProps) {
  const [copied, setCopied] = useState(false);

  const [commNotes, setCommNotes] = useState<AppointmentLogJoin[]>([]);
  const [activityLogs, setActivityLogs] = useState<AppointmentLogJoin[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteAudience, setNoteAudience] = useState<NoteAudience[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const isVendor = userRole === "vendor";

  const loadLogs = async (appointmentId: string) => {
    setLogsLoading(true);
    try {
      const logs = await AppointmentsRepository.listHistory(appointmentId);
      setCommNotes(logs.filter(isNote));
      setActivityLogs(logs.filter((log) => !isNote(log)));
    } catch (e: any) {
      console.error("Failed to load logs:", e);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (open && appt?.id) {
      loadLogs(appt.id);
    } else {
      setCommNotes([]);
      setActivityLogs([]);
      setNoteText("");
      setNoteAudience([]);
    }
  }, [open, appt?.id]);

  const handleAddNote = async () => {
    if (!appt || !noteText.trim()) return;
    setSubmitting(true);
    try {
      await AppointmentsRepository.addNote(appt.id, noteText.trim(), isVendor ? noteAudience : undefined);
      toast.success("Note added");
      setNoteText("");
      setNoteAudience([]);
      await loadLogs(appt.id);
    } catch (e: any) {
      toast.error(e.message || "Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  if (!appt) return null;

  const status = STATUS_CONFIG[appt.status] ?? STATUS_CONFIG.pending;
  const metaData = (appt.meta_data || {}) as AppointmentMetaData;

  const hasReassignmentRequest =
    appt.status === appointment_status.pending &&
    metaData.reassignment_requested_by &&
    metaData.reassignment_request_reason;

  const isCancelled =
    appt.status === appointment_status.cancelled &&
    appt.meta_data &&
    (appt.meta_data as any).cancellation_notes;

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(appt.id);
      setCopied(true);
      toast.success("Booking ID copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0 gap-0">

        {/* ── Status banner ─────────────────────────────────────────────── */}
        <div
          className={cn(
            "flex items-center gap-2.5 px-5 py-3 border-b shrink-0",
            status.bg,
            status.text
          )}
        >
          {status.icon}
          <span className="text-sm font-semibold">{status.label}</span>
        </div>

        {/* ── Property hero ─────────────────────────────────────────────── */}
        <div className="px-5 pt-5 pb-4 border-b shrink-0 bg-muted/30">
          <div className="flex items-start gap-4">
            <Avatar className="w-14 h-14 rounded-xl shrink-0 shadow-sm">
              <AvatarImage
                src={
                  appt.property?.image ??
                  "https://app.shynenetwork.com/assets/signupAsset/images/apartment_default.jpg"
                }
                className="object-cover"
              />
              <AvatarFallback className="rounded-xl text-lg font-bold">
                {appt.property.name[0]}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <p className="font-semibold text-base leading-tight">{appt.property.name}</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Unit #{appt.property.unit_number}&nbsp;·&nbsp;{appt.property.beds} bd&nbsp;/&nbsp;
                {appt.property.baths} ba
              </p>
              {appt.property.address && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {appt.property.address}
                </p>
              )}
            </div>
          </div>

          {/* Copyable booking ID */}
          <button
            onClick={handleCopyId}
            className="mt-3 flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-foreground transition-colors group"
          >
            <span>Booking #{appt.id.slice(0, 8).toUpperCase()}</span>
            {copied ? (
              <Check className="w-3 h-3 text-green-500" />
            ) : (
              <Copy className="w-3 h-3 opacity-40 group-hover:opacity-100 transition-opacity" />
            )}
          </button>
        </div>

        {/* ── Stats strip ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 divide-x border-b shrink-0">
          <div className="flex flex-col items-center justify-center py-3 px-2 gap-0.5">
            <span className="text-base font-bold tabular-nums">
              {appt.final_price ? `$${appt.final_price.toFixed(0)}` : "TBD"}
            </span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Price</span>
          </div>
          <div className="flex flex-col items-center justify-center py-3 px-2 gap-0.5">
            <span className="text-base font-bold">{moment(appt.date).format("MMM D, YYYY")}</span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Scheduled</span>
          </div>
        </div>

        {/* ── Scrollable body ───────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">

          <Section title="Service">
            <Row label="Category" value={appt.service.parent_name || "—"} />
            <Row label="Service" value={appt.service.name} />
            <Row
              label="Price"
              value={appt.final_price ? `$${appt.final_price.toFixed(2)}` : "TBD"}
            />
          </Section>

          <Section title="Assignment">
            <Row label="Vendor" value={appt.vendor.name} />
            {assignmentSlot ? (
              <SlotRow label="Assign individual">{assignmentSlot}</SlotRow>
            ) : (
              appt.assigned_user?.name && (
                <Row label="Assigned to" value={appt.assigned_user.name} />
              )
            )}
            {appt.assigned_organization?.display_name && (
              <Row label="Sub org" value={appt.assigned_organization.display_name} />
            )}
          </Section>

          <Section title="Timeline">
            <Row label="Booked on" value={moment(appt.created_at).format("MMM DD, YYYY")} />
            <Row label="Scheduled" value={moment(appt.date).format("MMM DD, YYYY")} />
          </Section>

          {appt.work_order_notes && (
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-0.5">
                Work Order Notes
              </p>
              <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg border px-3 py-2.5 leading-relaxed">
                {appt.work_order_notes}
              </p>
            </div>
          )}

          {/* ── Communication Log ──────────────────────────────────────── */}
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-0.5">
              Communication Log
            </p>

            <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
              <Textarea
                placeholder="Add a note..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={2}
                className="resize-none text-sm bg-background"
              />
              {isVendor && <NoteAudiencePicker audience={noteAudience} onChange={setNoteAudience} idPrefix="quick-view-audience" />}
              <Button
                size="sm"
                className="w-full h-7 text-xs"
                disabled={!noteText.trim() || submitting}
                onClick={handleAddNote}
              >
                {submitting ? "Adding..." : "Add Note"}
              </Button>
            </div>

            {logsLoading && (
              <p className="text-xs text-muted-foreground px-0.5">Loading...</p>
            )}
            {!logsLoading && commNotes.length === 0 && (
              <p className="text-xs text-muted-foreground px-0.5">No notes yet.</p>
            )}
            {commNotes.map((note) => (
              <div key={note.id} className="rounded-lg border bg-background px-3 py-2.5 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-muted-foreground">
                    {note.user?.name ?? "Unknown"} · {format(new Date(note.created_at), "MMM d, h:mm a")}
                  </span>
                  {note.audience !== undefined && (
                    <Badge variant="outline" className="text-[9px] py-0 px-1">
                      {audienceLabel(note.audience)}
                    </Badge>
                  )}
                </div>
                <p className="text-xs leading-relaxed">{note.message}</p>
              </div>
            ))}
          </div>

          {/* ── Activity Log ───────────────────────────────────────────── */}
          {activityLogs.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-0.5">
                Activity Log
              </p>
              <div className="space-y-1.5">
                {activityLogs.map((log) => (
                  <div key={log.id} className="flex gap-2.5 text-xs">
                    <div className="w-px bg-border shrink-0 ml-1.5 mt-1.5" />
                    <div className="pb-1.5 min-w-0">
                      <p className="text-muted-foreground leading-relaxed">{log.message}</p>
                      <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                        {log.user?.name ? `${log.user.name} · ` : ""}
                        {format(new Date(log.created_at), "MMM d, yyyy h:mm a")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasReassignmentRequest && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 px-3 py-2.5 space-y-1">
              <p className="text-[11px] font-semibold text-amber-800 dark:text-amber-200 uppercase tracking-wide">
                Reassignment Requested
              </p>
              <p className="text-sm text-amber-900 dark:text-amber-100">
                {metaData.reassignment_request_reason as string}
              </p>
            </div>
          )}

          {isCancelled && (
            <div className="rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 px-3 py-2.5 space-y-1">
              <p className="text-[11px] font-semibold text-red-800 dark:text-red-200 uppercase tracking-wide">
                Cancellation Reason
              </p>
              <p className="text-sm text-red-900 dark:text-red-100">
                {(appt.meta_data as any).cancellation_notes}
              </p>
            </div>
          )}
        </div>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <div className="px-4 py-4 border-t bg-background shrink-0">
          <Button asChild className="w-full">
            <Link to={`/appointments/${appt.id}`}>
              Open Full Detail
              <ExternalLink className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>

      </SheetContent>
    </Sheet>
  );
}
